"use client"

import { BffApiClientMolecule } from "@/app/api-client/store"
import { ItemPreview } from "@/app/nft/offer-dialog/item-preview"
import {
  PriceQuote,
  QuoteItem,
  QuoteItemSkeleton,
} from "@/app/nft/price-quote/price-quote"
import { PriceWrapper } from "@/app/price"
import {
  useEvmServiceFee,
  useSolanaServiceFee,
} from "@/app/sdk/action-molecules/eclipse/fees-molecule"
import { WalletConnectMoleculeReatom } from "@/app/wallet-connect/store.reatom"
import type { ItemOffer, OpenSeaOfferRef } from "@anaconda/core/bid/types"
import type { Nft } from "@anaconda/core/nft/types"
import type { OpenSeaOfferFulfillment } from "@anaconda/core/opensea/offer-fulfillment"
import type { PriceValue } from "@anaconda/core/price/types"
import { Trans } from "@lingui/react/macro"
import {
  Button,
  Card,
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@rarible_int/anaconda_ui_components"
import { reatomComponent } from "@reatom/react"
import { useQuery } from "@tanstack/react-query"
import { useMolecule } from "bunshi/react"
import { useMemo } from "react"
import { readOpenSeaAcceptQuote } from "./open-sea-accept-quote"

interface Props {
  nft: Nft
  offer: ItemOffer
  /** Which fee schedule a Rarible bid is accepted under. */
  chain: "evm" | "solana"
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: () => void
}

/**
 * Shows what accepting an offer pays before anything is signed: the price,
 * the fees taken out of it, and what the seller receives.
 */
export function AcceptOfferDialog({
  nft,
  offer,
  chain,
  open,
  onOpenChange,
  onConfirm,
}: Props) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[396px]" aria-describedby={undefined}>
        {open ? (
          <div className="flex flex-col gap-y-2">
            <DialogHeader className="pb-2">
              <DialogTitle className="text-center text-lg">
                <Trans>Accept offer</Trans>
              </DialogTitle>
            </DialogHeader>
            <ItemPreview nft={nft} />
            {offer.openSea ? (
              <OpenSeaQuote
                nft={nft}
                offer={offer}
                openSea={offer.openSea}
                onConfirm={onConfirm}
              />
            ) : chain === "evm" ? (
              <EvmRaribleQuote nft={nft} offer={offer} onConfirm={onConfirm} />
            ) : (
              <SolanaRaribleQuote
                nft={nft}
                offer={offer}
                onConfirm={onConfirm}
              />
            )}
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  )
}

interface QuoteProps {
  nft: Nft
  offer: ItemOffer
  onConfirm: () => void
}

// One component per fee hook, so each branch calls a fixed set of hooks.
const EvmRaribleQuote = reatomComponent<QuoteProps>(
  ({ nft, offer, onConfirm }) => {
    const serviceFeeQuery = useEvmServiceFee(
      nft.collection.blockchain,
      offer.bid.price,
    )

    return (
      <>
        <PriceQuote
          nftId={nft.id}
          price={offer.bid.price}
          serviceFeeQuery={serviceFeeQuery}
        />
        <ConfirmButton onConfirm={onConfirm} />
      </>
    )
  },
  "EvmRaribleQuote",
)

const SolanaRaribleQuote = reatomComponent<QuoteProps>(
  ({ nft, offer, onConfirm }) => {
    const serviceFeeQuery = useSolanaServiceFee(
      nft.collection.blockchain,
      offer.bid.price,
    )

    return (
      <>
        <PriceQuote
          nftId={nft.id}
          price={offer.bid.price}
          serviceFeeQuery={serviceFeeQuery}
        />
        <ConfirmButton onConfirm={onConfirm} />
      </>
    )
  },
  "SolanaRaribleQuote",
)

const OpenSeaQuote = reatomComponent<QuoteProps & { openSea: OpenSeaOfferRef }>(
  ({ nft, offer, openSea, onConfirm }) => {
    const bffApiClient = useMolecule(BffApiClientMolecule)
    const { evmAddress } = useMolecule(WalletConnectMoleculeReatom)
    const fulfiller = evmAddress()

    const quoteQuery = useQuery({
      queryKey: ["nfts", "opensea-accept-quote", openSea.orderHash, fulfiller],
      enabled: !!fulfiller,
      // A fulfilment is short-lived; never reuse one across openings.
      staleTime: 0,
      gcTime: 0,
      retry: false,
      queryFn: async () => {
        const response = await bffApiClient.api.nfts.opensea[
          "offer-fulfillment"
        ].$post({
          json: {
            itemId: nft.id,
            orderHash: openSea.orderHash,
            protocolAddress: openSea.protocolAddress,
            fulfiller: fulfiller ?? "",
          },
        })
        if (!response.ok) throw { status: response.status }

        const { fulfillment } = await response.json()
        return fulfillment as OpenSeaOfferFulfillment
      },
    })

    const quote = useMemo(() => {
      if (!quoteQuery.data || !fulfiller) return undefined
      try {
        return readOpenSeaAcceptQuote({
          fulfillment: quoteQuery.data,
          offer: openSea,
          nft,
          fulfiller,
          currency: offer.bid.price.currency,
        })
      } catch {
        return null
      }
    }, [quoteQuery.data, fulfiller, openSea, nft, offer.bid.price.currency])

    return (
      <>
        <Card className="flex flex-col gap-2 p-4">
          {quote ? (
            <>
              <div className="flex flex-col gap-1 border-b pb-2 text-2xs">
                <QuoteItem
                  name={<Trans>Price</Trans>}
                  value={<QuotePrice {...quote.gross} />}
                />
                <QuoteItem
                  name={<Trans>OpenSea and creator fees</Trans>}
                  value={<QuotePrice {...quote.fees} />}
                />
              </div>
              <QuoteItem
                name={<Trans>You will receive</Trans>}
                value={<QuotePrice {...quote.sellerReceives} />}
              />
            </>
          ) : quote === null || quoteQuery.isError ? (
            <p className="text-destructive text-sm">
              <OpenSeaQuoteError status={errorStatus(quoteQuery.error)} />
            </p>
          ) : (
            <>
              <QuoteItemSkeleton />
              <QuoteItemSkeleton />
              <QuoteItemSkeleton />
            </>
          )}
        </Card>
        <p className="px-1 text-foreground/60 text-xs">
          <Trans>
            The first time you sell into an OpenSea offer from this collection,
            you'll be asked to approve OpenSea to transfer it.
          </Trans>
        </p>
        <ConfirmButton onConfirm={onConfirm} disabled={!quote} />
      </>
    )
  },
  "OpenSeaQuote",
)

function OpenSeaQuoteError({ status }: { status: number | undefined }) {
  if (status === 409) {
    return <Trans>This OpenSea offer is no longer available.</Trans>
  }
  if (status === 503) {
    return <Trans>OpenSea offers are temporarily unavailable.</Trans>
  }
  return <Trans>OpenSea could not prepare this sale.</Trans>
}

function errorStatus(error: unknown): number | undefined {
  return typeof error === "object" &&
    error !== null &&
    "status" in error &&
    typeof error.status === "number"
    ? error.status
    : undefined
}

function QuotePrice(props: PriceValue) {
  return <PriceWrapper currencyClassName="text-foreground" {...props} />
}

function ConfirmButton({
  onConfirm,
  disabled,
}: {
  onConfirm: () => void
  disabled?: boolean
}) {
  return (
    <DialogClose asChild>
      <Button type="button" onClick={onConfirm} disabled={disabled}>
        <Trans>Accept offer</Trans>
      </Button>
    </DialogClose>
  )
}
